import type {
  GlobalPatientMatch,
  GlobalPatientSearchResponse,
  ConsentRequestPayload,
  ConsentRequestResponse,
  ConsentPageDto,
  SharedHistoryDto,
  ConsentListItem,
} from '../../../types';
import { fetchApi, type ApiResponse } from '../client';
import { globalMatchesOf } from '../../globalPatientMatches';

export const consentModule = {
  // Global patient search
  searchGlobalPatient: (params: { mobile?: string; name?: string; dob?: string; abhaId?: string }) =>
    fetchApi<GlobalPatientSearchResponse>('/api/v1/patients/global/search', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  /**
   * Who else on ClinVea has records for this mobile (medopd-docs 122): one entry per person, masked,
   * the requesting clinic excluded. Never throws — the endpoint is rate-limited (a 429 throws inside
   * fetchApi) and callers treat any failure as "nothing found" rather than blocking registration.
   */
  searchGlobalMatchesByMobile: async (mobile: string): Promise<ApiResponse<GlobalPatientMatch[]>> => {
    try {
      const res = await fetchApi<GlobalPatientSearchResponse>('/api/v1/patients/global/search', {
        method: 'POST',
        body: JSON.stringify({ mobile }),
        skipBodyLog: true,
      });
      if (res.error) return { error: res.error, errorCode: res.errorCode };
      return { data: globalMatchesOf(res.data) };
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Global search failed' };
    }
  },

  // Create consent request (OTP or QR)
  requestConsent: (payload: ConsentRequestPayload) =>
    fetchApi<ConsentRequestResponse>('/api/v1/patients/consents/request', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Verify OTP
  verifyOtp: (consentId: string, otp: string) =>
    fetchApi<Record<string, unknown>>('/api/v1/patients/consents/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ consentId, otp }),
    }),

  // QR page (public — patient's phone)
  getQrConsentPage: (token: string) =>
    fetchApi<ConsentPageDto>(`/api/v1/patients/consents/qr/${token}`),

  // QR approval code to the patient's registered mobile (public — patient's phone; medopd-docs 107)
  sendQrCode: (token: string) =>
    fetchApi<{ sent: boolean; maskedDestination: string; expiresInSeconds: number }>('/api/v1/patients/consents/qr/send-code', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  // QR approve (public — patient's phone)
  approveQr: (payload: {
    token: string;
    signedName: string;
    dobConfirmed?: string;
    signatureDataUrl?: string;
    /** The code sent to the patient's registered mobile — required. */
    otp?: string;
  }) =>
    fetchApi<Record<string, unknown>>('/api/v1/patients/consents/qr/approve', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Revoke consent (staff)
  revokeConsent: (consentId: string, reason?: string) =>
    fetchApi<Record<string, unknown>>('/api/v1/patients/consents/revoke', {
      method: 'POST',
      body: JSON.stringify({ consentId, reason }),
    }),

  // Revoke via patient self-service token
  revokeByToken: (revokeToken: string, reason?: string) =>
    fetchApi<Record<string, unknown>>('/api/v1/patients/consents/revoke', {
      method: 'POST',
      body: JSON.stringify({ revokeToken, reason }),
    }),

  // Get shared history for a patient
  getSharedHistory: (clinicPatientId: string) =>
    fetchApi<SharedHistoryDto>(`/api/v1/patients/${clinicPatientId}/shared-history`),

  // List all consents for current clinic
  listConsents: () =>
    fetchApi<ConsentListItem[]>('/api/v1/patients/consents'),
};
